import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { Box, Divider, Typography, CircularProgress } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import PatientDatas from "src/modules/medcard/patient-datas";
import OpenCloseSection from "./components/open-close-section";
import PrevDiseaseListCard from "./components/PREVIUS_DISEASE/list-card";
import PrevDiseaseModal from "./components/PREVIUS_DISEASE";
import PrevSurgeriesListCard from "./components/PREVIUS_SURGERIES/list-card";
import PrevSurgeryModal from "./components/PREVIUS_SURGERIES";
import FamilyHistoryListCard from "./components/FAMILY_HISTORY/list-card";
import FamilyHistoryModal from "./components/FAMILY_HISTORY";
import AllergyCardList from "./components/ALLERGIES/card-list";
import AllergyModal from "./components/ALLERGIES";
import DiseasesCardList from "./components/DISEASES/card-list";
import DiseasesModal from "./components/DISEASES";
import HabitsCardList from "./components/HABITS/card-list";
import HabitsModal from "./components/HABITS";
import DrugsCardList from "./components/DRUGS/card-list";
import DrugsModal from "./components/DRUGS";
import ExposuresCardList from "./components/EXPOSURES/card-list";
import ExposuresModal from "./components/EXPOSURES";
import SpecialNotesCardList from "./components/SPECIAL_NOTES/card-list";
import SpecialNotesModal from "./components/SPECIAL_NOTES";
import BloodPressureCardList from "./components/BLOOD_PRESSURE/card-list";
import BloodPressureModal from "./components/BLOOD_PRESSURE";
import RareDiseasesCardList from "./components/RARE_DISEASES/card-list";
import RareDiseasesModal from "./components/RARE_DISEASES";
import HeightWeightCardList from "./components/HEIGHT_WEIGHT/card-list";
import OtherCardList from "./components/OTHER/card-list";
import PregnantCardList from "./components/PREGNANT/card-list";
import { mcGeneralDiseaseService, McGeneralDiseaseGroup, McGeneralDiseaseItem, GeneralPatientInfoData } from "src/api/medcards/mcGeneralDiseaseService";
import { healthInfoUserViewService } from "src/api/medcards/healthInfoUserViewService";

interface GeneralHealthInformationModalProps {
  open: boolean;
  onClose: () => void;
  patientId?: string | number;
  patientName?: string;
  initialSection?: string;
}

const GeneralHealthInformationModal: React.FC<GeneralHealthInformationModalProps> = ({
  open,
  onClose,
  patientId,
  patientName,
  initialSection,
}) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state: any) => state.auth);

  // Açılır/kapanır section state'leri
  const [isPregnantOpen, setIsPregnantOpen] = useState(false);
  const [isHeightOpen, setIsHeightOpen] = useState(false);
  const [isPrevDiseaseOpen, setIsPrevDiseaseOpen] = useState(false);
  const [isPrevSurgeryOpen, setIsPrevSurgeryOpen] = useState(false);
  const [isFamilyHistoryOpen, setIsFamilyHistoryOpen] = useState(false);
  const [isAllergiesOpen, setIsAllergiesOpen] = useState(false);
  const [isHabitsOpen, setIsHabitsOpen] = useState(false);
  const [isDrugsOpen, setIsDrugsOpen] = useState(false);
  const [isExposuresOpen, setIsExposuresOpen] = useState(false);
  const [isSpecialNotesOpen, setIsSpecialNotesOpen] = useState(false);
  const [isBloodPressureOpen, setIsBloodPressureOpen] = useState(false);
  const [isRareDiseasesOpen, setIsRareDiseasesOpen] = useState(false);
  const [isOtherOpen, setIsOtherOpen] = useState(false);

  // Section ref'leri - scroll için
  const sectionRefs = {
    allergies: useRef<HTMLDivElement>(null),
    habits: useRef<HTMLDivElement>(null),
    pregnancy: useRef<HTMLDivElement>(null),
    'prev-diseases': useRef<HTMLDivElement>(null),
    'prev-surgeries': useRef<HTMLDivElement>(null),
    'family-history': useRef<HTMLDivElement>(null),
    diseases: useRef<HTMLDivElement>(null),
    drugs: useRef<HTMLDivElement>(null),
    exposures: useRef<HTMLDivElement>(null),
    'special-notes': useRef<HTMLDivElement>(null),
    'blood-pressure': useRef<HTMLDivElement>(null),
    'rare-diseases': useRef<HTMLDivElement>(null),
    'height-weight': useRef<HTMLDivElement>(null),
    other: useRef<HTMLDivElement>(null),
  };

  // Initial section logic - section'ı aç ve scroll et
  useEffect(() => {
    if (open && initialSection) {
      // Tümünü kapat
      setIsPregnantOpen(false);
      setIsHeightOpen(false);
      setIsPrevDiseaseOpen(false);
      setIsPrevSurgeryOpen(false);
      setIsFamilyHistoryOpen(false);
      setIsAllergiesOpen(false);
      setIsHabitsOpen(false);
      setIsDrugsOpen(false);
      setIsExposuresOpen(false);
      setIsSpecialNotesOpen(false);
      setIsBloodPressureOpen(false);
      setIsRareDiseasesOpen(false);
      setIsOtherOpen(false);

      // İstenen bölümü aç
      switch (initialSection) {
        case 'allergies': setIsAllergiesOpen(true); break;
        case 'habits': setIsHabitsOpen(true); break;
        case 'pregnancy': setIsPregnantOpen(true); break;
        case 'prev-diseases': setIsPrevDiseaseOpen(true); break;
        case 'prev-surgeries': setIsPrevSurgeryOpen(true); break;
        case 'family-history': setIsFamilyHistoryOpen(true); break;
        case 'diseases': setIsDiseasesOpen(true); break;
        case 'drugs': setIsDrugsOpen(true); break;
        case 'exposures': setIsExposuresOpen(true); break;
        case 'special-notes': setIsSpecialNotesOpen(true); break;
        case 'blood-pressure': setIsBloodPressureOpen(true); break;
        case 'rare-diseases': setIsRareDiseasesOpen(true); break;
        case 'height-weight': setIsHeightOpen(true); break;
        case 'other': setIsOtherOpen(true); break;
      }

      // Section'a scroll et (DOM güncellenince)
      const ref = sectionRefs[initialSection as keyof typeof sectionRefs];
      if (ref) {
        setTimeout(() => {
          ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    }
  }, [open, initialSection]);
  const [openPrevDiseaseModal, setOpenPrevDiseaseModal] = useState(false);
  const [editPrevDiseaseData, setEditPrevDiseaseData] = useState<McGeneralDiseaseItem | null>(null);
  const [openPrevSurgeryModal, setOpenPrevSurgeryModal] = useState(false);
  const [editPrevSurgeryData, setEditPrevSurgeryData] = useState<McGeneralDiseaseItem | null>(null);
  const [openFamilyHistoryModal, setOpenFamilyHistoryModal] = useState(false);
  const [editFamilyHistoryData, setEditFamilyHistoryData] = useState<McGeneralDiseaseItem | null>(null);
  const [openAllergyModal, setOpenAllergyModal] = useState(false);
  const [editAllergyData, setEditAllergyData] = useState<McGeneralDiseaseItem | null>(null);
  const [isDiseasesOpen, setIsDiseasesOpen] = useState(false);
  const [openDiseasesModal, setOpenDiseasesModal] = useState(false);
  const [editDiseasesData, setEditDiseasesData] = useState<McGeneralDiseaseItem | null>(null);
  const [openHabitsModal, setOpenHabitsModal] = useState(false);
  const [editHabitsData, setEditHabitsData] = useState<McGeneralDiseaseItem | null>(null);
  const [openDrugsModal, setOpenDrugsModal] = useState(false);
  const [editDrugsData, setEditDrugsData] = useState<McGeneralDiseaseItem | null>(null);
  const [openExposuresModal, setOpenExposuresModal] = useState(false);
  const [editExposuresData, setEditExposuresData] = useState<McGeneralDiseaseItem | null>(null);
  const [openSpecialNotesModal, setOpenSpecialNotesModal] = useState(false);
  const [editSpecialNotesData, setEditSpecialNotesData] = useState<McGeneralDiseaseItem | null>(null);
  const [openBloodPressureModal, setOpenBloodPressureModal] = useState(false);
  const [editBloodPressureData, setEditBloodPressureData] = useState<McGeneralDiseaseItem | null>(null);
  const [openRareDiseasesModal, setOpenRareDiseasesModal] = useState(false);
  const [editRareDiseasesData, setEditRareDiseasesData] = useState<McGeneralDiseaseItem | null>(null);

  // Form data states (kullanıcı değişiklikleri)
  const [heightWeightData, setHeightWeightData] = useState<any>(null);
  const [otherData, setOtherData] = useState<any>(null);
  const [pregnantData, setPregnantData] = useState<any>(null);

  // Saved data states (save edilmiş değerler - diğer alanlar kaydedilirken bunlar kullanılacak)
  const [savedHeightWeightData, setSavedHeightWeightData] = useState<any>(null);
  const [savedOtherData, setSavedOtherData] = useState<any>(null);
  const [savedPregnantData, setSavedPregnantData] = useState<any>(null);

  // API data state
  const [generalDiseasesData, setGeneralDiseasesData] = useState<{
    counts: Record<string, number>;
    groups: McGeneralDiseaseGroup[];
  } | null>(null);
  const [generalPatientInfo, setGeneralPatientInfo] = useState<GeneralPatientInfoData | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");

  // Save loading states
  const [savingPregnancy, setSavingPregnancy] = useState(false);
  const [savingHeightWeight, setSavingHeightWeight] = useState(false);
  const [savingOther, setSavingOther] = useState(false);

  // User preferences state (checkbox states for each typeId)
  const [userPreferences, setUserPreferences] = useState<Set<number>>(new Set([1, 2, 10])); // Default: sabit tipler
  const [orderedPreferences, setOrderedPreferences] = useState<number[]>([1, 2, 10]); // API'den gelen sıra

  // Fetch general diseases data
  const fetchGeneralDiseases = useCallback(() => {
    if (patientId) {
      setIsLoadingData(true);
      mcGeneralDiseaseService
        .getMcGeneralDiseases(patientId)
        .then((response) => {
          setGeneralDiseasesData({
            counts: response.counts,
            groups: response.groups,
          });
        })
        .catch((error) => {
          console.error("Error fetching general diseases:", error);
          setGeneralDiseasesData(null);
        })
        .finally(() => {
          setIsLoadingData(false);
        });
    }
  }, [patientId]);

  // Fetch general patient info (Boy Kilo, Gebelik, Diğer)
  const fetchGeneralPatientInfo = useCallback(() => {
    if (patientId) {
      const ptid = typeof patientId === 'string' ? parseInt(patientId) : patientId;
      mcGeneralDiseaseService
        .getGeneralPatientInfo(ptid)
        .then((response) => {
          if (response.issuccess && response.data) {
            setGeneralPatientInfo(response.data);

            // utime'ı parse et (API'den gelen utime değerini kullan)
            const operationDate = (response.data as any).utime
              ? new Date((response.data as any).utime)
              : new Date();

            // Boy Kilo verilerini set et
            if (response.data.patlength || response.data.weight) {
              const bmi = response.data.patlength && response.data.weight
                ? (response.data.weight / Math.pow(response.data.patlength / 100, 2)).toFixed(2)
                : "";

              const heightWeightDataObj = {
                height: response.data.patlength?.toString() || "",
                weight: response.data.weight?.toString() || "",
                bmi: bmi,
                operationDate: operationDate,
                operator: user?.username || "",
              };
              setHeightWeightData(heightWeightDataObj);
              setSavedHeightWeightData(heightWeightDataObj); // Save edilmiş değer olarak da set et
            }

            // Gebelik verilerini set et (pregnantorlactation her zaman bir değer olabilir: "Y", "H", "S", "E", "D" veya null)
            let pregnantDataObj;
            if (response.data.pregnantorlactation !== null && response.data.pregnantorlactation !== undefined) {
              pregnantDataObj = {
                pregnancyStatus: response.data.pregnantorlactation,
                operationDate: operationDate,
                operator: user?.username || "",
              };
            } else {
              // Veri yoksa boş set et
              pregnantDataObj = {
                pregnancyStatus: "",
                operationDate: operationDate,
                operator: user?.username || "",
              };
            }
            setPregnantData(pregnantDataObj);
            setSavedPregnantData(pregnantDataObj); // Save edilmiş değer olarak da set et

            // Diğer verilerini set et
            const otherDataObj = {
              psychologicalStatus: response.data.psychologicalstatus?.toString() || "",
              marriageStatus: response.data.marriage?.toString() || "",
              geneticExpertOpinion: response.data.geneticexpertopinion?.toString() || "",
              operationDate: operationDate,
              operator: user?.username || "",
            };
            setOtherData(otherDataObj);
            setSavedOtherData(otherDataObj); // Save edilmiş değer olarak da set et
          }
        })
        .catch((error) => {
          console.error("Error fetching general patient info:", error);
        });
    }
  }, [patientId, user]);

  // Fetch user preferences for health info types
  const fetchUserPreferences = useCallback(() => {
    // Get userId from Redux state
    const userId = user?.id;

    if (!userId) {
      setUserPreferences(new Set([1, 2, 10])); // Default: sabit tipler
      setOrderedPreferences([1, 2, 10]);
      return;
    }

    healthInfoUserViewService
      .getUserPreferences(userId)
      .then((response) => {
        if (response.issuccess && response.data?.typeids) {
          const typeIds = response.data.typeids;
          setUserPreferences(new Set(typeIds));
          setOrderedPreferences(typeIds);
        } else {
          setUserPreferences(new Set([1, 2, 10]));
          setOrderedPreferences([1, 2, 10]);
        }
      })
      .catch((error) => {
        console.error("Error fetching user preferences:", error);
        setUserPreferences(new Set([1, 2, 10]));
        setOrderedPreferences([1, 2, 10]);
      });
  }, [user]);

  useEffect(() => {
    if (open && patientId) {
      fetchGeneralDiseases();
      fetchGeneralPatientInfo();
      fetchUserPreferences();
    }
  }, [open, patientId, fetchGeneralDiseases, fetchGeneralPatientInfo, fetchUserPreferences]);

  // Helper function to get group by typename
  const getGroupByTypeName = useCallback(
    (typeName: string): McGeneralDiseaseGroup | undefined => {
      return generalDiseasesData?.groups?.find((g) => g.typename === typeName);
    },
    [generalDiseasesData]
  );

  // Helper function to get count by typename
  const getCountByTypeName = useCallback(
    (typeName: string): number => {
      return generalDiseasesData?.counts?.[typeName] || 0;
    },
    [generalDiseasesData]
  );

  const togglePregnant = useCallback(() => setIsPregnantOpen((p) => !p), []);
  const toggleHeight = useCallback(() => setIsHeightOpen((p) => !p), []);
  const togglePrevDisease = useCallback(
    () => setIsPrevDiseaseOpen((p) => !p),
    []
  );
  const togglePrevSurgery = useCallback(
    () => setIsPrevSurgeryOpen((p) => !p),
    []
  );
  const toggleFamilyHistory = useCallback(
    () => setIsFamilyHistoryOpen((p) => !p),
    []
  );
  const toggleAllergies = useCallback(
    () => setIsAllergiesOpen((p) => !p),
    []
  );
  const toggleHabits = useCallback(
    () => setIsHabitsOpen((p) => !p),
    []
  );
  const toggleDrugs = useCallback(
    () => setIsDrugsOpen((p) => !p),
    []
  );
  const toggleDiseases = useCallback(
    () => setIsDiseasesOpen((p) => !p),
    []
  );
  const toggleExposures = useCallback(
    () => setIsExposuresOpen((p) => !p),
    []
  );
  const toggleSpecialNotes = useCallback(
    () => setIsSpecialNotesOpen((p) => !p),
    []
  );
  const toggleBloodPressure = useCallback(
    () => setIsBloodPressureOpen((p) => !p),
    []
  );
  const toggleRareDiseases = useCallback(
    () => setIsRareDiseasesOpen((p) => !p),
    []
  );
  const toggleOther = useCallback(
    () => setIsOtherOpen((p) => !p),
    []
  );

  // Helper function to handle preference changes
  const handlePreferenceChange = useCallback((typeId: number, checked: boolean) => {
    const newPrefs = new Set(userPreferences);
    if (checked) {
      newPrefs.add(typeId);
    } else {
      newPrefs.delete(typeId);
    }
    setUserPreferences(newPrefs);
  }, [userPreferences]);

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          position: "relative",
          py: { xs: 2, sm: 3 },
          px: { xs: 1.5, sm: 2 },
          width: "100%",
          maxWidth: "100%",
          height: "80vh",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t("modal.title").toUpperCase()}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <ByzMuiCloseButton onClick={onClose} />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {(savingPregnancy || savingHeightWeight || savingOther) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: "100%" },
              mx: "auto",
              px: { xs: 0, md: 0 },
            }}
          >
            <PatientDatas patientId={patientId} isCreateMedCard noMargin />

            {/* Gebelik Durumu */}
            <Box mt={2} ref={sectionRefs['pregnancy']}>
              <OpenCloseSection
                title={t("sections.pregnancy")}
                isOpen={isPregnantOpen}
                onToggle={togglePregnant}
                showSaveButton
                saveLoading={savingPregnancy}
                typeId={10}
                showInMainPanel={userPreferences.has(10)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(10, checked)}
                onSaveClick={async () => {
                  if (!patientId) {
                    alert("Hasta ID bulunamadı");
                    return;
                  }

                  const ptid = typeof patientId === 'string' ? parseInt(patientId) : patientId;

                  try {
                    setSavingPregnancy(true);
                    await mcGeneralDiseaseService.updateGeneralPatientInfo({
                      ptid,
                      pregnantorlactation: pregnantData?.pregnancyStatus || null,
                      // Diğer alanların save edilmiş değerlerini gönder (değiştirilip save edilmemiş değerler değil)
                      patlength: savedHeightWeightData?.height ? parseFloat(savedHeightWeightData.height) : null,
                      weight: savedHeightWeightData?.weight ? parseFloat(savedHeightWeightData.weight) : null,
                      psychologicalstatus: savedOtherData?.psychologicalStatus ? parseInt(savedOtherData.psychologicalStatus) : null,
                      marriage: savedOtherData?.marriageStatus ? parseInt(savedOtherData.marriageStatus) : null,
                      geneticexpertopinion: savedOtherData?.geneticExpertOpinion ? parseInt(savedOtherData.geneticExpertOpinion) : null,
                    });
                    // Save edilen değeri kaydet
                    setSavedPregnantData(pregnantData);
                    setSnackbarMessage("Gebelik Durumu başarıyla kaydedildi");
                    setSnackbarType("success");
                    setSnackbarOpen(true);
                    fetchGeneralPatientInfo(); // Verileri yenile
                  } catch (error) {
                    console.error("Error saving pregnancy status:", error);
                    setSnackbarMessage("Gebelik Durumu kaydedilirken bir hata oluştu");
                    setSnackbarType("error");
                    setSnackbarOpen(true);
                  } finally {
                    setSavingPregnancy(false);
                  }
                }}
                count={pregnantData ? 1 : 0}
              >
                {isPregnantOpen && (
                  <PregnantCardList
                    onDataChange={(data: any) => setPregnantData(data)}
                    initialData={pregnantData}
                    pregnancyStatusOptions={generalPatientInfo?.pregnancystatusoptions || []}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Boy Kilo */}
            <Box mt={2} ref={sectionRefs['height-weight']}>
              <OpenCloseSection
                title={t("sections.height-weight")}
                isOpen={isHeightOpen}
                onToggle={toggleHeight}
                showSaveButton
                saveLoading={savingHeightWeight}
                typeId={8}
                showInMainPanel={userPreferences.has(8)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(8, checked)}
                onSaveClick={async () => {
                  if (!patientId) {
                    setSnackbarMessage("Hasta ID bulunamadı");
                    setSnackbarType("error");
                    setSnackbarOpen(true);
                    return;
                  }

                  if (!heightWeightData?.height || !heightWeightData?.weight) {
                    setSnackbarMessage("Boy Ve Kilo bilgisinin birlikte girilmesi zorunludur. Aksi durumda bilgilerin E-Nabız gönderimi yapılmayacaktır.");
                    setSnackbarType("error");
                    setSnackbarOpen(true);
                    return;
                  }

                  const ptid = typeof patientId === 'string' ? parseInt(patientId) : patientId;

                  try {
                    setSavingHeightWeight(true);
                    await mcGeneralDiseaseService.updateGeneralPatientInfo({
                      ptid,
                      patlength: heightWeightData.height ? parseFloat(heightWeightData.height) : null,
                      weight: heightWeightData.weight ? parseFloat(heightWeightData.weight) : null,
                      // Gebelik ve Diğer verilerinin save edilmiş değerlerini gönder (değiştirilip save edilmemiş değerler değil)
                      pregnantorlactation: savedPregnantData?.pregnancyStatus || null,
                      psychologicalstatus: savedOtherData?.psychologicalStatus ? parseInt(savedOtherData.psychologicalStatus) : null,
                      marriage: savedOtherData?.marriageStatus ? parseInt(savedOtherData.marriageStatus) : null,
                      geneticexpertopinion: savedOtherData?.geneticExpertOpinion ? parseInt(savedOtherData.geneticExpertOpinion) : null,
                    });
                    // Save edilen değeri kaydet
                    setSavedHeightWeightData(heightWeightData);
                    setSnackbarMessage("Boy Kilo ve diğer bilgiler başarıyla kaydedildi");
                    setSnackbarType("success");
                    setSnackbarOpen(true);
                    fetchGeneralPatientInfo(); // Verileri yenile
                  } catch (error) {
                    console.error("Error saving height weight:", error);
                    setSnackbarMessage("Boy Kilo kaydedilirken bir hata oluştu");
                    setSnackbarType("error");
                    setSnackbarOpen(true);
                  } finally {
                    setSavingHeightWeight(false);
                  }
                }}
                count={heightWeightData ? 1 : 0}
              >
                {isHeightOpen && (
                  <HeightWeightCardList
                    onDataChange={(data: any) => setHeightWeightData(data)}
                    initialData={heightWeightData}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Geçirilmiş Hastalıklar */}
            <Box mt={2} ref={sectionRefs['prev-diseases']}>
              <OpenCloseSection
                title={t("sections.prev-diseases")}
                isOpen={isPrevDiseaseOpen}
                onToggle={togglePrevDisease}
                showAddButton
                typeId={11}
                showInMainPanel={userPreferences.has(11)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(11, checked)}
                onAddClick={() => {
                  setEditPrevDiseaseData(null);
                  setOpenPrevDiseaseModal(true);
                }}
                count={getCountByTypeName("2-Geçirilmiş Hastalıklar")}
              >
                {isPrevDiseaseOpen && (
                  <PrevDiseaseListCard
                    data={getGroupByTypeName("2-Geçirilmiş Hastalıklar")?.items || []}
                    onDelete={() => fetchGeneralDiseases()}
                    onEdit={(item) => {
                      setEditPrevDiseaseData(item);
                      setOpenPrevDiseaseModal(true);
                    }}
                    onDeleteSuccess={() => {
                      setSnackbarMessage(t("prev-disease.delete-success"));
                      setSnackbarType("success");
                      setSnackbarOpen(true);
                    }}
                    onDeleteError={() => {
                      setSnackbarMessage(t("prev-disease.delete-error"));
                      setSnackbarType("error");
                      setSnackbarOpen(true);
                    }}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Geçirilmiş Ameliyatlar */}
            <Box mt={2} ref={sectionRefs['prev-surgeries']}>
              <OpenCloseSection
                title={t("sections.prev-surgeries")}
                isOpen={isPrevSurgeryOpen}
                onToggle={togglePrevSurgery}
                showAddButton
                typeId={12}
                showInMainPanel={userPreferences.has(12)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(12, checked)}
                onAddClick={() => {
                  setEditPrevSurgeryData(null);
                  setOpenPrevSurgeryModal(true);
                }}
                count={getCountByTypeName("3-Geçirilmiş Ameliyatlar")}
              >
                {isPrevSurgeryOpen && (
                  <PrevSurgeriesListCard
                    data={getGroupByTypeName("3-Geçirilmiş Ameliyatlar")?.items || []}
                    onDelete={() => fetchGeneralDiseases()}
                    onEdit={(item) => {
                      setEditPrevSurgeryData(item);
                      setOpenPrevSurgeryModal(true);
                    }}
                    onDeleteSuccess={() => {
                      setSnackbarMessage(t("prev-surgery.delete-success"));
                      setSnackbarType("success");
                      setSnackbarOpen(true);
                      fetchGeneralDiseases();
                    }}
                    onDeleteError={() => {
                      setSnackbarMessage(t("prev-surgery.delete-error"));
                      setSnackbarType("error");
                      setSnackbarOpen(true);
                    }}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Soy Geçmiş */}
            <Box mt={2} ref={sectionRefs['family-history']}>
              <OpenCloseSection
                title={t("sections.family-history")}
                isOpen={isFamilyHistoryOpen}
                onToggle={toggleFamilyHistory}
                showAddButton
                typeId={7}
                showInMainPanel={userPreferences.has(7)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(7, checked)}
                onAddClick={() => {
                  setEditFamilyHistoryData(null);
                  setOpenFamilyHistoryModal(true);
                }}
                count={getCountByTypeName("4-Soygeçmiş")}
              >
                {isFamilyHistoryOpen && (
                  <FamilyHistoryListCard
                    data={getGroupByTypeName("4-Soygeçmiş")?.items || []}
                    onDelete={() => fetchGeneralDiseases()}
                    onEdit={(item) => {
                      setEditFamilyHistoryData(item);
                      setOpenFamilyHistoryModal(true);
                    }}
                    onDeleteSuccess={() => {
                      setSnackbarMessage(t("family-history.delete-success"));
                      setSnackbarType("success");
                      setSnackbarOpen(true);
                    }}
                    onDeleteError={() => {
                      setSnackbarMessage(t("family-history.delete-error"));
                      setSnackbarType("error");
                      setSnackbarOpen(true);
                    }}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Alerjiler */}
            <Box mt={2} ref={sectionRefs['allergies']}>
              <OpenCloseSection
                title={t("sections.allergies")}
                isOpen={isAllergiesOpen}
                onToggle={toggleAllergies}
                showAddButton
                typeId={1}
                showInMainPanel={userPreferences.has(1)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(1, checked)}
                onAddClick={() => {
                  setEditAllergyData(null);
                  setOpenAllergyModal(true);
                }}
                count={getCountByTypeName("5-Alerjiler (Vademecum)")}
              >
                {isAllergiesOpen && (
                  <AllergyCardList
                    data={getGroupByTypeName("5-Alerjiler (Vademecum)")?.items || []}
                    onDelete={() => fetchGeneralDiseases()}
                    onEdit={(item) => {
                      setEditAllergyData(item);
                      setOpenAllergyModal(true);
                    }}
                    onDeleteSuccess={() => {
                      setSnackbarMessage(t("allergy.delete-success"));
                      setSnackbarType("success");
                      setSnackbarOpen(true);
                    }}
                    onDeleteError={() => {
                      setSnackbarMessage(t("allergy.delete-error"));
                      setSnackbarType("error");
                      setSnackbarOpen(true);
                    }}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Hastalıklar */}
            <Box mt={2} ref={sectionRefs['diseases']}>
              <OpenCloseSection
                title={t("sections.diseases")}
                isOpen={isDiseasesOpen}
                onToggle={toggleDiseases}
                showAddButton
                typeId={4}
                showInMainPanel={userPreferences.has(4)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(4, checked)}
                onAddClick={() => {
                  setEditDiseasesData(null);
                  setOpenDiseasesModal(true);
                }}
                count={getCountByTypeName("6-Hastalıklar")}
              >
                {isDiseasesOpen && (
                  <DiseasesCardList
                    data={getGroupByTypeName("6-Hastalıklar")?.items || []}
                    onDelete={() => fetchGeneralDiseases()}
                    onEdit={(item) => {
                      setEditDiseasesData(item);
                      setOpenDiseasesModal(true);
                    }}
                    onDeleteSuccess={() => {
                      setSnackbarMessage(t("disease.delete-success"));
                      setSnackbarType("success");
                      setSnackbarOpen(true);
                    }}
                    onDeleteError={() => {
                      setSnackbarMessage(t("disease.delete-error"));
                      setSnackbarType("error");
                      setSnackbarOpen(true);
                    }}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Alışkanlıklar */}
            <Box mt={2} ref={sectionRefs['habits']}>
              <OpenCloseSection
                title={t("sections.habits")}
                isOpen={isHabitsOpen}
                onToggle={toggleHabits}
                showAddButton
                typeId={2}
                showInMainPanel={userPreferences.has(2)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(2, checked)}
                onAddClick={() => {
                  setEditHabitsData(null);
                  setOpenHabitsModal(true);
                }}
                count={getCountByTypeName("7-Alışkanlıklar")}
              >
                {isHabitsOpen && (
                  <HabitsCardList
                    data={getGroupByTypeName("7-Alışkanlıklar")?.items || []}
                    onDelete={() => fetchGeneralDiseases()}
                    onEdit={(item) => {
                      setEditHabitsData(item);
                      setOpenHabitsModal(true);
                    }}
                    onDeleteSuccess={() => {
                      setSnackbarMessage(t("habit.delete-success"));
                      setSnackbarType("success");
                      setSnackbarOpen(true);
                    }}
                    onDeleteError={() => {
                      setSnackbarMessage(t("habit.delete-error"));
                      setSnackbarType("error");
                      setSnackbarOpen(true);
                    }}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Kullandığı İlaçlar */}
            <Box mt={2} ref={sectionRefs['drugs']}>
              <OpenCloseSection
                title={t("sections.drugs")}
                isOpen={isDrugsOpen}
                onToggle={toggleDrugs}
                showAddButton
                typeId={5}
                showInMainPanel={userPreferences.has(5)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(5, checked)}
                onAddClick={() => {
                  setEditDrugsData(null);
                  setOpenDrugsModal(true);
                }}
                count={getCountByTypeName("8-Kullandığı İlaçlar")}
              >
                {isDrugsOpen && (
                  <DrugsCardList
                    data={getGroupByTypeName("8-Kullandığı İlaçlar")?.items || []}
                    onDelete={() => fetchGeneralDiseases()}
                    onEdit={(item) => {
                      setEditDrugsData(item);
                      setOpenDrugsModal(true);
                    }}
                    onDeleteSuccess={() => {
                      setSnackbarMessage(t("drug.delete-success"));
                      setSnackbarType("success");
                      setSnackbarOpen(true);
                    }}
                    onDeleteError={() => {
                      setSnackbarMessage(t("drug.delete-error"));
                      setSnackbarType("error");
                      setSnackbarOpen(true);
                    }}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Maruziyetler */}
            <Box mt={2} ref={sectionRefs['exposures']}>
              <OpenCloseSection
                title={t("sections.exposures")}
                isOpen={isExposuresOpen}
                onToggle={toggleExposures}
                showAddButton
                typeId={6}
                showInMainPanel={userPreferences.has(6)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(6, checked)}
                onAddClick={() => {
                  setEditExposuresData(null);
                  setOpenExposuresModal(true);
                }}
                count={getCountByTypeName("9-Maruziyetler")}
              >
                {isExposuresOpen && (
                  <ExposuresCardList
                    data={getGroupByTypeName("9-Maruziyetler")?.items || []}
                    onDelete={() => fetchGeneralDiseases()}
                    onEdit={(item) => {
                      setEditExposuresData(item);
                      setOpenExposuresModal(true);
                    }}
                    onDeleteSuccess={() => {
                      setSnackbarMessage(t("exposure.delete-success"));
                      setSnackbarType("success");
                      setSnackbarOpen(true);
                    }}
                    onDeleteError={() => {
                      setSnackbarMessage(t("exposure.delete-error"));
                      setSnackbarType("error");
                      setSnackbarOpen(true);
                    }}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Özel Notlar */}
            <Box mt={2} ref={sectionRefs['special-notes']}>
              <OpenCloseSection
                title={t("sections.special-notes")}
                isOpen={isSpecialNotesOpen}
                onToggle={toggleSpecialNotes}
                showAddButton
                typeId={14}
                showInMainPanel={userPreferences.has(14)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(14, checked)}
                onAddClick={() => {
                  setEditSpecialNotesData(null);
                  setOpenSpecialNotesModal(true);
                }}
                count={getCountByTypeName("1-Özel Notlar")}
              >
                {isSpecialNotesOpen && (
                  <SpecialNotesCardList
                    data={getGroupByTypeName("1-Özel Notlar")?.items || []}
                    onDelete={() => fetchGeneralDiseases()}
                    onEdit={(item) => {
                      setEditSpecialNotesData(item);
                      setOpenSpecialNotesModal(true);
                    }}
                    onDeleteSuccess={() => {
                      setSnackbarMessage(t("special-note.delete-success"));
                      setSnackbarType("success");
                      setSnackbarOpen(true);
                    }}
                    onDeleteError={() => {
                      setSnackbarMessage(t("special-note.delete-error"));
                      setSnackbarType("error");
                      setSnackbarOpen(true);
                    }}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Tansiyon Takibi */}
            <Box mt={2} ref={sectionRefs['blood-pressure']}>
              <OpenCloseSection
                title={t("sections.blood-pressure")}
                isOpen={isBloodPressureOpen}
                onToggle={toggleBloodPressure}
                showAddButton
                typeId={3}
                showInMainPanel={userPreferences.has(3)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(3, checked)}
                onAddClick={() => {
                  setEditBloodPressureData(null);
                  setOpenBloodPressureModal(true);
                }}
                count={getCountByTypeName("-Tansiyon Takibi")}
              >
                {isBloodPressureOpen && (
                  <BloodPressureCardList
                    data={getGroupByTypeName("-Tansiyon Takibi")?.items || []}
                    onDelete={() => fetchGeneralDiseases()}
                    onEdit={(item) => {
                      setEditBloodPressureData(item);
                      setOpenBloodPressureModal(true);
                    }}
                    onDeleteSuccess={() => {
                      setSnackbarMessage(t("blood-pressure.delete-success"));
                      setSnackbarType("success");
                      setSnackbarOpen(true);
                    }}
                    onDeleteError={() => {
                      setSnackbarMessage(t("blood-pressure.delete-error"));
                      setSnackbarType("error");
                      setSnackbarOpen(true);
                    }}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Nadir Hastalıklar */}
            <Box mt={2} ref={sectionRefs['rare-diseases']}>
              <OpenCloseSection
                title={t("sections.rare-diseases")}
                isOpen={isRareDiseasesOpen}
                onToggle={toggleRareDiseases}
                showAddButton
                typeId={13}
                showInMainPanel={userPreferences.has(13)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(13, checked)}
                onAddClick={() => {
                  setEditRareDiseasesData(null);
                  setOpenRareDiseasesModal(true);
                }}
                count={getCountByTypeName("11-Nadir Hastalıklar")}
              >
                {isRareDiseasesOpen && (
                  <RareDiseasesCardList
                    data={getGroupByTypeName("11-Nadir Hastalıklar")?.items || []}
                    onDelete={() => fetchGeneralDiseases()}
                    onEdit={(item) => {
                      setEditRareDiseasesData(item);
                      setOpenRareDiseasesModal(true);
                    }}
                    onDeleteSuccess={() => {
                      setSnackbarMessage(t("rare-disease.delete-success"));
                      setSnackbarType("success");
                      setSnackbarOpen(true);
                    }}
                    onDeleteError={() => {
                      setSnackbarMessage(t("rare-disease.delete-error"));
                      setSnackbarType("error");
                      setSnackbarOpen(true);
                    }}
                  />
                )}
              </OpenCloseSection>
            </Box>

            {/* Diğer */}
            <Box mt={2} ref={sectionRefs['other']}>
              <OpenCloseSection
                title={t("sections.other")}
                isOpen={isOtherOpen}
                onToggle={toggleOther}
                showSaveButton
                typeId={9}
                showInMainPanel={userPreferences.has(9)}
                onShowInMainPanelChange={(checked) => handlePreferenceChange(9, checked)}
                saveLoading={savingOther}
                onSaveClick={async () => {
                  if (!patientId) {
                    setSnackbarMessage("Hasta ID bulunamadı");
                    setSnackbarType("error");
                    setSnackbarOpen(true);
                    return;
                  }

                  const ptid = typeof patientId === 'string' ? parseInt(patientId) : patientId;

                  try {
                    setSavingOther(true);
                    await mcGeneralDiseaseService.updateGeneralPatientInfo({
                      ptid,
                      psychologicalstatus: otherData?.psychologicalStatus ? parseInt(otherData.psychologicalStatus) : null,
                      marriage: otherData?.marriageStatus ? parseInt(otherData.marriageStatus) : null,
                      geneticexpertopinion: otherData?.geneticExpertOpinion ? parseInt(otherData.geneticExpertOpinion) : null,
                      // Gebelik ve Boy Kilo verilerinin save edilmiş değerlerini gönder (değiştirilip save edilmemiş değerler değil)
                      pregnantorlactation: savedPregnantData?.pregnancyStatus || null,
                      patlength: savedHeightWeightData?.height ? parseFloat(savedHeightWeightData.height) : null,
                      weight: savedHeightWeightData?.weight ? parseFloat(savedHeightWeightData.weight) : null,
                    });
                    // Save edilen değeri kaydet
                    setSavedOtherData(otherData);
                    setSnackbarMessage("Diğer bilgiler başarıyla kaydedildi");
                    setSnackbarType("success");
                    setSnackbarOpen(true);
                    fetchGeneralPatientInfo(); // Verileri yenile
                  } catch (error) {
                    console.error("Error saving other data:", error);
                    setSnackbarMessage("Diğer bilgiler kaydedilirken bir hata oluştu");
                    setSnackbarType("error");
                    setSnackbarOpen(true);
                  } finally {
                    setSavingOther(false);
                  }
                }}
                count={otherData ? 1 : 0}
              >
                {isOtherOpen && (
                  <OtherCardList
                    onDataChange={(data: any) => setOtherData(data)}
                    initialData={otherData}
                    psychologicalStatusOptions={generalPatientInfo?.psychologicalstatusoptions || []}
                    marriageOptions={generalPatientInfo?.marriageoptions || []}
                    genExpertOpinionOptions={generalPatientInfo?.genexpertopinionoptions || []}
                  />
                )}
              </OpenCloseSection>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Geçirilmiş Hastalık Ekle Modalı */}
      <PrevDiseaseModal
        open={openPrevDiseaseModal}
        onClose={() => {
          setOpenPrevDiseaseModal(false);
          setEditPrevDiseaseData(null);
          fetchGeneralDiseases();
        }}
        patientId={patientId}
        editData={editPrevDiseaseData}
      />

      {/* Geçirilmiş Ameliyat Ekle Modalı */}
      <PrevSurgeryModal
        open={openPrevSurgeryModal}
        onClose={() => {
          setOpenPrevSurgeryModal(false);
          setEditPrevSurgeryData(null);
          fetchGeneralDiseases();
        }}
        patientId={patientId}
        editData={editPrevSurgeryData}
      />

      {/* Soy Geçmiş Ekle Modalı */}
      <FamilyHistoryModal
        open={openFamilyHistoryModal}
        onClose={() => {
          setOpenFamilyHistoryModal(false);
          setEditFamilyHistoryData(null);
          fetchGeneralDiseases();
        }}
        patientId={patientId}
        editData={editFamilyHistoryData}
      />

      {/* Alerji Ekle Modalı */}
      <AllergyModal
        open={openAllergyModal}
        onClose={() => {
          setOpenAllergyModal(false);
          setEditAllergyData(null);
          fetchGeneralDiseases();
        }}
        patientId={patientId}
        editData={editAllergyData}
      />

      {/* Hastalık Ekle Modalı */}
      <DiseasesModal
        open={openDiseasesModal}
        onClose={() => {
          setOpenDiseasesModal(false);
          setEditDiseasesData(null);
          fetchGeneralDiseases();
        }}
        patientId={patientId}
        editData={editDiseasesData}
      />

      {/* Alışkanlık Ekle Modalı */}
      <HabitsModal
        open={openHabitsModal}
        onClose={() => {
          setOpenHabitsModal(false);
          setEditHabitsData(null);
          fetchGeneralDiseases();
        }}
        patientId={patientId}
        editData={editHabitsData}
      />

      {/* Kullandığı İlaç Ekle Modalı */}
      <DrugsModal
        open={openDrugsModal}
        onClose={() => {
          setOpenDrugsModal(false);
          setEditDrugsData(null);
          fetchGeneralDiseases();
        }}
        patientId={patientId}
        editData={editDrugsData}
      />

      {/* Maruziyet Ekle Modalı */}
      <ExposuresModal
        open={openExposuresModal}
        onClose={() => {
          setOpenExposuresModal(false);
          setEditExposuresData(null);
          fetchGeneralDiseases();
        }}
        patientId={patientId}
        editData={editExposuresData}
      />

      {/* Özel Not Ekle Modalı */}
      <SpecialNotesModal
        open={openSpecialNotesModal}
        onClose={() => {
          setOpenSpecialNotesModal(false);
          setEditSpecialNotesData(null);
          fetchGeneralDiseases();
        }}
        patientId={patientId}
        editData={editSpecialNotesData}
      />

      {/* Tansiyon Takibi Ekle Modalı */}
      <BloodPressureModal
        open={openBloodPressureModal}
        onClose={() => {
          setOpenBloodPressureModal(false);
          setEditBloodPressureData(null);
          fetchGeneralDiseases();
        }}
        patientId={patientId}
        editData={editBloodPressureData}
      />

      {/* Nadir Hastalık Ekle Modalı */}
      <RareDiseasesModal
        open={openRareDiseasesModal}
        onClose={() => {
          setOpenRareDiseasesModal(false);
          setEditRareDiseasesData(null);
          fetchGeneralDiseases();
        }}
        patientId={patientId}
        editData={editRareDiseasesData}
      />

      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title={snackbarType === "success" ? "Başarılı" : "Hata"}
        message={snackbarMessage}
        type={snackbarType}
      />
    </OpenComponentDialog>
  );
};

export default GeneralHealthInformationModal;

